<template><div><dt-notice kind="warning" :show-close="false" class="d-wmx100p d-my-200 dialtone-doc-notice">
Before using background color utilities, first consider <a href="/design/colors/palette/#surface" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">semantic surface colors</a>.
</dt-notice>
<h2 id="usage" tabindex="-1" class="d-docsite--header-2">Usage <a class="header-anchor d-link d-docsite--link d-link" href="#usage" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Use <code v-pre>d-bgc-{color}</code> to set an element's background color.</p>
<code-example source-code='&lt;div class=&quot;d-p-200 d-bar-300 d-bgc-primary&quot;&gt;Primary&lt;/div&gt;
&lt;div class=&quot;d-p-200 d-bar-300 d-bgc-critical&quot;&gt;Critical&lt;/div&gt;'>
<dt-stack data-demo-wrapper gap="200" :direction="{ 'default': 'column', 'md': 'row' }">
  <div class="d-p-200 d-bar-300 d-bgc-primary">Primary</div>
  <div class="d-p-200 d-bar-300 d-bgc-critical">Critical</div>
</dt-stack>
</code-example>
<h2 id="hover" tabindex="-1" class="d-docsite--header-2">Hover <a class="header-anchor d-link d-docsite--link d-link" href="#hover" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Use <code v-pre>h:d-bgc-{color}</code> to change an element's <code v-pre>:hover</code> state background color.</p>
<code-example source-code='&lt;dt-button kind=&quot;unstyled&quot; class=&quot;d-p-200 d-bgc-primary h:d-bgc-critical&quot;&gt;
  Hover over me
&lt;/dt-button&gt;'>
<dt-button kind="unstyled" class="d-p-200 d-bgc-primary h:d-bgc-critical">
  Hover over me
</dt-button>
</code-example>
<h2 id="focus" tabindex="-1" class="d-docsite--header-2">Focus <a class="header-anchor d-link d-docsite--link d-link" href="#focus" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Use <code v-pre>f:d-bgc-{color}</code> to change an element's <code v-pre>:focus</code> and <code v-pre>:focus-within</code> state background color.</p>
<code-example source-code='&lt;dt-button kind=&quot;unstyled&quot; class=&quot;d-p-200 d-bgc-primary f:d-bgc-critical&quot;&gt;
  Focus me
&lt;/dt-button&gt;'>
<dt-button kind="unstyled" class="d-p-200 d-bgc-primary f:d-bgc-critical">
  Focus me
</dt-button>
</code-example>
<h2 id="focus-visible" tabindex="-1" class="d-docsite--header-2">Focus Visible <a class="header-anchor d-link d-docsite--link d-link" href="#focus-visible" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Use <code v-pre>fv:d-bgc-{color}</code> to change an element's <code v-pre>:focus-visible</code> state background color [only when focused by keyboard].</p>
<code-example source-code='&lt;dt-button kind=&quot;unstyled&quot; class=&quot;d-p-200 d-bgc-primary fv:d-bgc-critical&quot;&gt;
  Keyboard focus me
&lt;/dt-button&gt;'>
<dt-button kind="unstyled" class="d-p-200 d-bgc-primary fv:d-bgc-critical">
  Keyboard focus me
</dt-button>
</code-example>
<h2 id="changing-opacity" tabindex="-1" class="d-docsite--header-2">Changing Opacity <a class="header-anchor d-link d-docsite--link d-link" href="#changing-opacity" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Use <code v-pre>d-bgo{stop}</code> to change an element's background color opacity. You can also change the background color opacity on <code v-pre>:hover</code>, <code v-pre>:focus</code>, <code v-pre>:focus-visible</code> by using the respective <code v-pre>h:d-bgo{stop}</code>, <code v-pre>f:d-bgo{stop}</code>, <code v-pre>fv:d-bgo{stop}</code> prefixes.</p>
<code-example source-code='&lt;div class=&quot;d-p-100 d-bgc-critical d-bar-300 d-bgo100&quot;&gt;100%&lt;/div&gt;
&lt;div class=&quot;d-p-100 d-bgc-critical d-bar-300 d-bgo99&quot;&gt;99%&lt;/div&gt;
&lt;div class=&quot;d-p-100 d-bgc-critical d-bar-300 d-bgo95&quot;&gt;95%&lt;/div&gt;
&lt;div class=&quot;d-p-100 d-bgc-critical d-bar-300 d-bgo90&quot;&gt;90%&lt;/div&gt;
&lt;div class=&quot;d-p-100 d-bgc-critical d-bar-300 d-bgo75&quot;&gt;75%&lt;/div&gt;
&lt;div class=&quot;d-p-100 d-bgc-critical d-bar-300 d-bgo50&quot;&gt;50%&lt;/div&gt;
&lt;div class=&quot;d-p-100 d-bgc-critical d-bar-300 d-bgo25&quot;&gt;25%&lt;/div&gt;
&lt;div class=&quot;d-p-100 d-bgc-critical d-bar-300 d-bgo10&quot;&gt;10%&lt;/div&gt;
&lt;div class=&quot;d-p-100 d-bgc-critical d-bar-300 d-bgo0&quot;&gt;0%&lt;/div&gt;'>
<dt-stack data-demo-wrapper gap="200" :direction="{ 'default': 'column', 'md': 'row' }">
  <div class="d-p-100 d-bgc-critical d-bar-300 d-bgo100">100%</div>
  <div class="d-p-100 d-bgc-critical d-bar-300 d-bgo99">99%</div>
  <div class="d-p-100 d-bgc-critical d-bar-300 d-bgo95">95%</div>
  <div class="d-p-100 d-bgc-critical d-bar-300 d-bgo90">90%</div>
  <div class="d-p-100 d-bgc-critical d-bar-300 d-bgo75">75%</div>
  <div class="d-p-100 d-bgc-critical d-bar-300 d-bgo50">50%</div>
  <div class="d-p-100 d-bgc-critical d-bar-300 d-bgo25">25%</div>
  <div class="d-p-100 d-bgc-critical d-bar-300 d-bgo10">10%</div>
  <div class="d-p-100 d-bgc-critical d-bar-300 d-bgo0">0%</div>
</dt-stack>
</code-example>
<h2 id="inverted" tabindex="-1" class="d-docsite--header-2">Inverted <a class="header-anchor d-link d-docsite--link d-link" href="#inverted" aria-hidden="true">#</a></h2>
<dt-notice kind="info" title="Tip" :show-close="false" class="d-wmx100p d-my-200 dialtone-doc-notice">
Avoid <code v-pre>-inverted</code> utility variants. Use the <a href="/components/mode-island.html#inverting" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">v-dt-mode directive</a> with base classes instead -- it automatically resolves the correct colors for the current mode.
</dt-notice>
<code-example source-code='&lt;div v-dt-mode:invert class=&quot;d-p-200 d-bar-300 d-bgc-primary&quot;&gt;Primary&lt;/div&gt;
&lt;div v-dt-mode:invert class=&quot;d-p-200 d-bar-300 d-bgc-critical&quot;&gt;Critical&lt;/div&gt;'>
<dt-stack data-demo-wrapper gap="200" :direction="{ 'default': 'column', 'md': 'row' }">
  <div v-dt-mode:invert class="d-p-200 d-bar-300 d-bgc-primary">Primary</div>
  <div v-dt-mode:invert class="d-p-200 d-bar-300 d-bgc-critical">Critical</div>
</dt-stack>
</code-example>
<h2 id="classes" tabindex="-1" class="d-docsite--header-2">Classes <a class="header-anchor d-link d-docsite--link d-link" href="#classes" aria-hidden="true">#</a></h2>
<new-utility-class-table :classes="backgroundColors">
  <template #example="{ className }">
    <div
      class="d-fl-shrink0 d-size-75 d-bar-circle d-ba d-bc-moderate"
      :class="className"
    />
  </template>
</new-utility-class-table>
</div></template>


<script setup>
  import { inject } from 'vue';
  import { extractUtilityClasses } from '@utilities';

  /*Excluded classes that have incorrect naming, should be renamed to `d-bgclip` to avoid conflicts*/
  const excludedClasses = ['d-bgc-border-box', 'd-bgc-content-box', 'd-bgc-padding-box', 'd-bgc-text'];

  const utilityClassDocs = inject('utilityClassDocs');
  const backgroundColors = extractUtilityClasses(utilityClassDocs, 'd-bgc-');
</script>